/**

Status: Copied from tiny-game; merge and port to separate package.

*/

{MAX_SAFE_INTEGER} := Number

// ascii is a subset of utf-8
utf8Decoder := new TextDecoder 'utf-8'

/**
* Read or write bytes to a DataView, auto-advancing the position.
* DataView methods but with get/put instead of get/set.
* Combines byte and text decoding as well.
*/
export class DataStream
  byteLength: number
  byteView: Uint8Array<ArrayBuffer>
  view: DataView<ArrayBuffer>
  position: number

  @(buffer: ArrayBuffer)
    @byteLength = buffer.byteLength
    @byteView = new Uint8Array buffer
    @view = new DataView buffer
    @position = 0

  /**
  * Subarray of bytes to send over the network
  * Classic pattern is to call reset, write out the data, then pass the result
  * of `bytes` directly to the socket.
  */
  bytes()
    return @byteView.subarray 0, @position

  done()
    @position >= @byteLength

  reset()
    @position = 0

  getAscii(length: number)
    utf8Decoder.decode @getBytes(length)

  putAscii(str: string)
    bytes := new Uint8Array str.length

    str.split('').forEach (c, i) ->
      code := c.charCodeAt(0)

      if code >= 0x80
        throw new Error "Character out of range in '#{str}', index: #{i} char: #{c} (#{code} > #{0x80})"

      bytes[i] = code

    @putBytes bytes

    return str

  getBytes(length: number)
    p := @position
    result := @byteView.subarray p, p + length
    @position += length

    return result

  putBytes(bytes: ArrayLike<number>)
    @byteView.set(bytes, @position)
    @position += bytes.length

    return bytes

  putUint24(v: number)
    @putUint16 v >> 8
    @putUint8(v & 0xff)

    return v

  getUint24() // TODO: endianness?
    (@getUint16() << 8) + @getUint8()

  /**
  read a MIDI-style variable-length unsigned integer
  (big-endian value in groups of 7 bits,
  with top bit set to signify that another byte follows)
  */
  getVarUint()
    result .= 0
    loop
      b .= @getUint8()
      if b & 0x80
        result += (b & 0x7f)
        result *= 0x80
      else
        return result + b

  /**
  * Write a MIDI-style variable-length unsigned integer.
  */
  putVarUint(v: number)
    if v > MAX_SAFE_INTEGER
      throw new Error "Number out of range: #{v} > #{MAX_SAFE_INTEGER}"

    b .= 0x2000000000000 // 2^49

    while b > 1
      if v >= b
        @putUint8( (v / b) | 0x80 )
      b /= 0x80

    @putUint8(v & 0x7f)

  /**
  * Read a single byte as an unsigned 8-bit integer and advance the position.
  */
  getUint8()
    v := @view.getUint8(@position)
    @position += 1

    return v

  /**
  * Write a single byte as an unsigned 8-bit integer and advance the position.
  */
  putUint8(v: number)
    @view.setUint8(@position, v)
    @position += 1

    return v

  /**
  * Read a single byte as a signed 8-bit integer and advance the position.
  */
  getInt8()
    v := @view.getInt8(@position)
    @position += 1

    return v

  /**
  * Write a single byte as a signed 8-bit integer and advance the position.
  */
  putInt8(v: number)
    @view.setInt8(@position, v)
    @position += 1

    return v

  /**
  * Read a 16-bit unsigned integer and advance the position.
  */
  getUint16(littleEndian?: boolean)
    v := @view.getUint16(@position, littleEndian)
    @position += 2

    return v

  /**
  * Write a 16-bit unsigned integer and advance the position.
  */
  putUint16(v: number, littleEndian?: boolean)
    @view.setUint16(@position, v, littleEndian)
    @position += 2

    return v

  /**
  * Read a 16-bit signed integer and advance the position.
  */
  getInt16(littleEndian?: boolean)
    v := @view.getInt16(@position, littleEndian)
    @position += 2

    return v

  /**
  * Write a 16-bit signed integer and advance the position.
  */
  putInt16(v: number, littleEndian?: boolean)
    @view.setInt16(@position, v, littleEndian)
    @position += 2

    return v

  /**
  * Read a 32-bit unsigned integer and advance the position.
  */
  getUint32(littleEndian?: boolean)
    v := @view.getUint32(@position, littleEndian)
    @position += 4

    return v

  /**
  * Write a 32-bit unsigned integer and advance the position.
  */
  putUint32(v: number, littleEndian?: boolean)
    @view.setUint32(@position, v, littleEndian)
    @position += 4

    return v

  /**
  * Read a 32-bit signed integer and advance the position.
  */
  getInt32(littleEndian?: boolean)
    v := @view.getInt32(@position, littleEndian)
    @position += 4

    return v

  /**
  * Write a 32-bit signed integer and advance the position.
  */
  putInt32(v: number, littleEndian?: boolean)
    @view.setInt32(@position, v, littleEndian)
    @position += 4

    return v

  /**
  * Read a 32-bit float and advance the position.
  */
  getFloat32(littleEndian?: boolean)
    v := @view.getFloat32(@position, littleEndian)
    @position += 4

    return v

  /**
  * Write a 32-bit float and advance the position.
  */
  putFloat32(v: number, littleEndian?: boolean)
    @view.setFloat32(@position, v, littleEndian)
    @position += 4

    return v

  /**
  * Read a 64-bit float and advance the position.
  */
  getFloat64(littleEndian?: boolean)
    v := @view.getFloat64(@position, littleEndian)
    @position += 8

    return v

  /**
  * Write a 64-bit float and advance the position.
  */
  putFloat64(v: number, littleEndian?: boolean)
    @view.setFloat64(@position, v, littleEndian)
    @position += 8

    return v

export default DataStream
